import {
  SoftwareEnvironmentBuildStateEnum,
  CreatePackageSchemaSourceEnum,
  ArchitectureTypesEnum,
} from "@/api-client";
import { ApiClient } from "@/apiUtils";
import {
  SoftwareAliasContext,
  SoftwareSpecContextProvider,
} from "@/crud/software/context";
import { useSoftwareBuild, useSoftwareSpec } from "@/crud/software/hooks";
import { UserContext } from "@/crud/user/context";
import { Urls } from "@/domain/urls";
import { BuildStatusChip } from "@/pages/Software/SoftwareEnvironment/common";
import { SoftwareSpecificationDetails } from "@/pages/Software/SoftwareEnvironment/details";
import { InstalledSoftwareTable } from "@/pages/Software/SoftwareEnvironment/installed";
import { SoftwareBuildLogsTable } from "@/pages/Software/SoftwareEnvironment/logs";
import { ScanResultDetails } from "@/pages/Software/SoftwareEnvironment/scan";
import { LoadingScreen } from "@/shared-components/LoadingScreen";
import { Page } from "@/components/layout/page";
import {
  VerticalTabPanel,
  VerticalTabs,
} from "@/shared-components/VerticalTabs";
import { useWorkspaceContextSlug } from "@/utils/hooks";
import { Button, Card, CardContent, Stack } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { parse } from "yaml";

enum TabValue {
  details = "details",
  specification = "specification",
  installed = "installed",
  logs = "logs",
  builds = "builds",
  scan = "scan",
}

export const SoftwareBuildView = (): React.ReactElement => {
  const buildId = parseInt(useParams().buildId as string, 10);
  const workspaceSlug = useWorkspaceContextSlug();
  const { id: aliasId, name } = useContext(SoftwareAliasContext);
  const { data: build, isSuccess: buildLoaded } = useSoftwareBuild(buildId);
  const { data: fullSpec, isSuccess: specLoaded } = useSoftwareSpec(
    build?.specId,
  );
  const { user } = useContext(UserContext);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const isManualSenv = fullSpec?.rawConda || fullSpec?.rawPip;
  const isPackageSyncSenv = !isManualSenv && !fullSpec?.containerUri;

  const handleRebuild = async () => {
    if (!fullSpec || !specLoaded) {
      return;
    }

    try {
      setIsRebuilding(true);

      // Base schema shared by all environment types
      const baseSchema = {
        name,
        creatorId: fullSpec.creatorId,
        architecture: fullSpec.architecture || ArchitectureTypesEnum.X8664,
        gpuEnabled: fullSpec.gpuEnabled,
        enableExperimentalInstaller: true,
        forceRebuild: true,
      };

      let createSchema;

      if (isManualSenv) {
        createSchema = {
          ...baseSchema,
          packages: fullSpec.packages.map((pkg) => ({
            ...pkg,
            file: pkg.file?.id,
          })),
          rawPip: fullSpec.rawPip,
          rawConda: fullSpec.rawConda ? parse(fullSpec.rawConda) : undefined,
        };
      } else if (isPackageSyncSenv && fullSpec?.scanResult?.id) {
        // Call the API to approximate packages using the scan ID
        const approximateResult =
          await ApiClient.declarativeViewsSoftwareEnvironmentApproximateSoftwarePackages(
            {
              approximateSpec: {
                architecture:
                  fullSpec.architecture || ArchitectureTypesEnum.X8664,
                packages: [],
                scanId: fullSpec.scanResult.id,
              },
            },
          );

        createSchema = {
          ...baseSchema,
          packages: approximateResult
            .filter((p) => p.include)
            .map((p) => ({
              name: p.name,
              specifier: p.specifier || "",
              source: p.note?.includes("pip")
                ? CreatePackageSchemaSourceEnum.Pip
                : CreatePackageSchemaSourceEnum.Conda,
              conda_name: p.condaName,
              client_version: p.clientVersion,
              include: p.include,
              channel: p.channelUrl,
            })),
        };
      } else {
        return;
      }

      // Create new software environment with the constructed schema
      await ApiClient.declarativeViewsSoftwareEnvironmentSoftwareEnvCreate({
        account: workspaceSlug,
        createSoftwareEnvironmentSpecSchema: createSchema,
      });
      // Redirect to the alias page
      window.location.href = `/${Urls.Software}/alias/${aliasId}/account/${workspaceSlug}`;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to rebuild environment:", error);
      setIsRebuilding(false);
    }
  };

  const breadCrumbs = useMemo(
    () => [
      {
        text: "Software Environments",
        link: `/${Urls.Software}`,
      },
      {
        text: name,
        link: `/${Urls.Software}/alias/${aliasId}/account/${workspaceSlug}`,
      },
      { text: "build" },
    ],
    [name, aliasId, workspaceSlug],
  );

  return (
    <Page title={`Software Environments - ${name}`} breadCrumbs={breadCrumbs}>
      {!buildLoaded && !specLoaded && <LoadingScreen />}
      {specLoaded && buildLoaded && (
        <>
          {fullSpec.containerUri && (
            <Card>
              <CardContent>
                This environment is an image located at {fullSpec.containerUri}{" "}
                and is not managed by Coiled
              </CardContent>
            </Card>
          )}
          {fullSpec.latestBuildId && (
            <>
              <SoftwareSpecContextProvider spec={fullSpec}>
                <VerticalTabs
                  tabExtra={
                    <>
                      <Stack direction="column" spacing={1}>
                        <BuildStatusChip
                          sx={{
                            margin: (theme) => theme.spacing(1),
                          }}
                          status={build.state}
                          reason={build.reason}
                        />
                        {user.isStaff &&
                          build.state ===
                            SoftwareEnvironmentBuildStateEnum.Error && (
                            <Button
                              variant="primary"
                              color="primary"
                              disabled={isRebuilding}
                              onClick={handleRebuild}
                              sx={{ mt: 1 }}
                            >
                              {isRebuilding
                                ? "Rebuilding..."
                                : "Rebuild (Staff)"}
                            </Button>
                          )}
                      </Stack>
                    </>
                  }
                  defaultTab={
                    build.state !== SoftwareEnvironmentBuildStateEnum.Built
                      ? TabValue.details
                      : TabValue.installed
                  }
                >
                  <VerticalTabPanel
                    value={TabValue.scan}
                    label="Local Environment"
                  >
                    <CardContent>
                      <ScanResultDetails />
                    </CardContent>
                  </VerticalTabPanel>
                  <VerticalTabPanel
                    value={TabValue.details}
                    label="Requirements"
                  >
                    <CardContent>
                      <SoftwareSpecificationDetails />
                    </CardContent>
                  </VerticalTabPanel>
                  <VerticalTabPanel value={TabValue.logs} label="Build Logs">
                    <SoftwareBuildLogsTable buildId={buildId} />
                  </VerticalTabPanel>
                  <VerticalTabPanel
                    disabled={
                      build.state !== SoftwareEnvironmentBuildStateEnum.Built
                    }
                    value={TabValue.installed}
                    label="Installed"
                  >
                    <InstalledSoftwareTable build={build} />
                  </VerticalTabPanel>
                </VerticalTabs>
              </SoftwareSpecContextProvider>
            </>
          )}
        </>
      )}
    </Page>
  );
};
